import { createLazy } from '@zajno/common/lazy/light';
import { createManualPromise } from '@zajno/common/async/misc';
import { LazyQueue } from 'scripts/components/lazy/lazyLoadComponent';
import { suspend } from 'suspend-react';
import { ILogger } from '@zajno/common/logger';
import { Getter } from '@zajno/common/types/getter';

type Args<T, TPre = void> = {
    priority?: number;

    logger?: ILogger;

    /** enqueued loader which will run outside React contexts.
     *
     * Will be called only by LazyQueue according to priority and current queue state.
     *
     * If `useLazyLoader` is called before, it will halt until `preloader` will be called and resolved.
     */
    preloader: () => Promise<T>;

    /** Unique keys for the usage of `suspend` to distinguish */
    cacheKeys: Getter<string[]>;

    /** called before calling main 'suspend' */
    usePreHook?: () => TPre;

    /** called inside 'suspend' after `preloader` promise is resolved */
    loader?: (res: T, pre?: TPre) => T | Promise<T>;

    /** called after 'suspend' hook returned */
    usePostHook?: (res: T, pre?: TPre) => void;
};

/** Couples Lazy loading with React Suspend */
export function createLazyObjectSuspend<T, TPre>(args: Args<T, TPre>) {

    const logger = args.logger;

    const suspendPromise = createLazy(() => createManualPromise<T>());
    const wrapLoader = async () => {
        logger?.log('Starting preloading...');
        try {
            const result = await args.preloader();
            logger?.log('Preloading finished, resolving suspend...');
            suspendPromise.value.resolve(result);
        } catch (err) {
            logger?.error('Preloading failed, rejecting suspend...');
            suspendPromise.value.reject(err);
        }
    };

    LazyQueue.enqueue(wrapLoader, args.priority);

    return {
        useLazyLoader: () => {
            const pre = args.usePreHook?.();
            const res = suspend(async () => {
                try {
                    const r = await suspendPromise.value.promise;
                    const final = (await args.loader?.(r, pre)) ?? r;
                    logger?.log('Loading finished, resolving result', final);
                    return final;
                } catch (err) {
                    logger?.error('Loading failed, rejecting result', err);
                    throw err;
                }
            }, Getter.getValue(args.cacheKeys));
            args.usePostHook?.(res, pre);
            return res;
        },
    };
}
